import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  FilterCondition,
  FilterConditionField,
  FilterConditionOutput,
  FilterStructure,
  UseFilterConditionsReturn,
} from '../types';
import {
  conditionsToOutput,
  createInitialCondition,
  generateId,
  outputToConditions,
} from './utils';

/**
 * Hook personalizado para manejar las condiciones del filtro avanzado
 *
 * @param filterOptions - Configuración de opciones de filtro disponibles
 * @param onChange - Callback que se ejecuta cuando cambian las condiciones válidas
 * @param initialConditions - Condiciones iniciales opcionales para hidratar el estado
 * @returns Objeto con estado y funciones para manejar las condiciones
 */
export const useFilterConditions = (
  filterOptions: FilterStructure[],
  onChange?: (conditions: FilterConditionOutput[]) => void,
  initialConditions?: FilterConditionOutput[]
): UseFilterConditionsReturn => {
  // ============================================================================
  // ESTADO LOCAL
  // ============================================================================

  const [conditions, setConditions] = useState<FilterCondition[]>(() => {
    // 🆕 Usar condiciones iniciales si están disponibles
    if (initialConditions && initialConditions.length > 0) {
      return outputToConditions(initialConditions);
    }
    return [createInitialCondition()];
  });

  // ============================================================================
  // SINCRONIZACIÓN INICIAL (SOLO UNA VEZ)
  // ============================================================================

  // 🆕 Ref para controlar si ya se inicializó
  const initializedRef = useRef(false);

  // 🆕 Efecto para sincronizar SOLO la primera vez
  useEffect(() => {
    if (
      !initializedRef.current &&
      initialConditions &&
      initialConditions.length > 0
    ) {
      const newConditions = outputToConditions(initialConditions);
      setConditions(newConditions);
      initializedRef.current = true;
    }
  }, [initialConditions]);

  // ============================================================================
  // VALORES COMPUTADOS MEMOIZADOS
  // ============================================================================

  // 🎯 Memoizar filtros activos
  const activeFiltersCount = useMemo(
    () =>
      conditions.filter((c) => {
        if (!c.column || !c.action) return false;

        // Para acciones "is_null" y "is_not_null", no se requiere value
        if (c.action === 'is_null' || c.action === 'is_not_null') {
          return true;
        }

        // Para el resto de acciones, se requiere value
        return Boolean(c.value);
      }).length,
    [conditions]
  );

  // 🎯 Memoizar opciones de columnas para evitar recálculos
  const selectedColumns = useMemo(
    () => conditions.map((c) => c.column),
    [conditions]
  );

  // ============================================================================
  // DEBOUNCE Y EMISIÓN DE CAMBIOS
  // ============================================================================

  // 🎯 Debounce del onChange para evitar múltiples llamadas
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const emitChanges = useCallback(
    (cond: FilterCondition[]) => {
      // Limpiar timeout anterior
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Debounce de 150ms para evitar llamadas excesivas
      timeoutRef.current = setTimeout(() => {
        onChange?.(conditionsToOutput(cond));
      }, 150);
    },
    [onChange]
  );

  // ============================================================================
  // FUNCIONES DE MANIPULACIÓN DE CONDICIONES
  // ============================================================================

  // 🎯 Agregar nueva condición
  const addCondition = useCallback(
    (conditionData?: Partial<FilterCondition>) => {
      const newCondition = {
        ...createInitialCondition(),
        ...conditionData, // 🎯 Merge con datos proporcionados
        id: generateId(), // 🎯 Siempre generar nuevo ID
      };

      setConditions((prev) => {
        const updated = [...prev, newCondition];
        // Emitir cambios de forma asíncrona para no bloquear el UI
        requestAnimationFrame(() => emitChanges(updated));
        return updated;
      });
    },
    [emitChanges]
  );

  // 🎯 Remover condición existente
  const removeCondition = useCallback(
    (id: string) => {
      setConditions((prev) => {
        const updated =
          prev.length === 1
            ? [{ ...prev[0], column: '', action: '', value: '' }]
            : prev.filter((c) => c.id !== id);

        requestAnimationFrame(() => emitChanges(updated));
        return updated;
      });
    },
    [emitChanges]
  );

  // 🎯 Actualizar campo específico de una condición
  const updateCondition = useCallback(
    (id: string, field: FilterConditionField, newValue: string) => {
      setConditions((prev) => {
        const updated = prev.map((c) => {
          if (c.id === id) {
            const updatedCondition = { ...c, [field]: newValue };

            // Si cambia la columna, resetear acción y valor
            if (field === 'column') {
              updatedCondition.action = '';
              updatedCondition.value = '';

              // Auto-seleccionar primera acción disponible
              const selectedFilter = filterOptions.find(
                (f) => f.column.value === newValue
              );
              if (selectedFilter && selectedFilter.actions.length > 0) {
                updatedCondition.action = selectedFilter.actions[0].value;
              }
            }
            return updatedCondition;
          }
          return c;
        });

        requestAnimationFrame(() => emitChanges(updated));
        return updated;
      });
    },
    [filterOptions, emitChanges]
  );

  // ============================================================================
  // CLEANUP DE RECURSOS
  // ============================================================================

  // 🎯 Cleanup del timeout al desmontar
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // ============================================================================
  // RETORNO DEL HOOK
  // ============================================================================

  return {
    conditions,
    activeFiltersCount,
    selectedColumns,
    addCondition,
    removeCondition,
    updateCondition,
  };
};
